// Experience templates — curated worlds users can clone into their own editable
// world. A template is a world with settings.is_template = true and an optional
// settings.experience_type.
import { supabase } from '../config/supabase';

export type ExperienceType = 'campfire' | 'group' | 'conference' | 'studio' | 'podcast' | 'concert' | 'cinema' | 'other';

export interface ExperienceMeta {
  type: ExperienceType;
  label: string;
  blurb: string;
}

// Catalog of experience kinds shown in the gallery + the publish picker.
export const EXPERIENCE_TYPES: ExperienceMeta[] = [
  { type: 'campfire', label: 'Campfire', blurb: 'Intimate circle, warm light, open voice' },
  { type: 'group', label: 'Group Room', blurb: 'Meet, share a screen, talk it out' },
  { type: 'conference', label: 'Conference Rooms', blurb: 'Stage, big screen, a seat at the table for everyone' },
  { type: 'studio', label: 'Studio', blurb: 'Mics, slides, stage lights — go on-air' },
  { type: 'podcast', label: 'Podcast', blurb: 'Hosts on stage, audience listens in' },
  { type: 'concert', label: 'Concert', blurb: 'Stage, big screen, lights, a crowd' },
  { type: 'cinema', label: 'Cinema', blurb: 'Big screen, tiered recliners, watch together' },
  { type: 'other', label: 'Experience', blurb: 'A custom space' },
];

export function experienceMeta(type?: string | null): ExperienceMeta {
  return EXPERIENCE_TYPES.find((e) => e.type === type) || EXPERIENCE_TYPES[EXPERIENCE_TYPES.length - 1];
}

export interface ExperienceWorld {
  id: string;
  name: string;
  thumbnail_url: string | null;
  experience_type: ExperienceType;
  owner_id: string;
  created_at: string;
}

interface WorldRow {
  id: string;
  name: string;
  thumbnail_url: string | null;
  owner_id: string;
  created_at: string;
  settings: { experience_type?: ExperienceType } | null;
}

function toExperience(w: WorldRow): ExperienceWorld {
  return {
    id: w.id,
    name: w.name,
    thumbnail_url: w.thumbnail_url,
    experience_type: (w.settings?.experience_type as ExperienceType) || 'other',
    owner_id: w.owner_id,
    created_at: w.created_at,
  };
}

// All published experience templates (newest first). Falls back to the world's
// current skybox image as the card thumbnail when no explicit thumbnail is set.
export async function fetchExperiences(): Promise<ExperienceWorld[]> {
  const { data, error } = await supabase
    .from('worlds')
    .select('id,name,thumbnail_url,owner_id,created_at,settings')
    .eq('settings->>is_template', 'true')
    .order('created_at', { ascending: false });
  if (error) throw error;
  const worlds = (data as WorldRow[]) || [];

  // Resolve each world's current skybox to use as a preview image.
  const ids = worlds.map((w) => w.id);
  const skyByWorld: Record<string, string> = {};
  if (ids.length) {
    const { data: skies } = await supabase
      .from('world_assets')
      .select('world_id,url,metadata')
      .eq('type', 'skybox')
      .in('world_id', ids);
    for (const sk of (skies as any[]) || []) {
      const img = sk.metadata?.thumbnail || sk.url;
      if (img && (!skyByWorld[sk.world_id] || sk.metadata?.isCurrent)) skyByWorld[sk.world_id] = img;
    }
  }

  // Prefer a captured card thumbnail (via the builder's Capture Thumbnail
  // button); fall back to the world's current skybox image when none is set.
  return worlds.map((w) => {
    const exp = toExperience(w);
    return { ...exp, thumbnail_url: w.thumbnail_url || skyByWorld[w.id] || null };
  });
}

// Clone a template into a new world owned by the caller; returns the new id.
export async function cloneExperience(sourceWorldId: string, newName?: string): Promise<string> {
  const { data, error } = await supabase.rpc('clone_experience', {
    p_source_world_id: sourceWorldId,
    p_new_name: newName ?? null,
  });
  if (error) throw error;
  return data as string;
}

// Owner action: publish one of your worlds as an experience template.
export async function publishAsExperience(worldId: string, type: ExperienceType): Promise<void> {
  const { data: rows, error: readErr } = await supabase.from('worlds').select('settings').eq('id', worldId).single();
  if (readErr) throw readErr;
  const settings = { ...((rows?.settings as Record<string, unknown>) || {}), is_template: true, experience_type: type, is_public: true };
  const { error } = await supabase.from('worlds').update({ settings, updated_at: new Date().toISOString() }).eq('id', worldId);
  if (error) throw error;
}

export async function unpublishExperience(worldId: string): Promise<void> {
  const { data: rows, error: readErr } = await supabase.from('worlds').select('settings').eq('id', worldId).single();
  if (readErr) throw readErr;
  const settings = { ...((rows?.settings as Record<string, unknown>) || {}), is_template: false };
  const { error } = await supabase.from('worlds').update({ settings, updated_at: new Date().toISOString() }).eq('id', worldId);
  if (error) throw error;
}
